import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminSidebar from '../../components/Layout/SuperAdminSidebar';
import stripeService from '../../services/stripeService';

// Import Material UI components
import {
  Box, Typography, Paper, Button, Divider, Alert, CircularProgress, Grid,
  Card, CardContent, ListItem, ListItemText, List, ListItemIcon,
  Chip, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TextField, InputAdornment, IconButton,
  Menu, MenuItem, Tooltip, TablePagination, Tab, Tabs, Snackbar,
  Container
} from '@mui/material';

// Import Material UI icons
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ReceiptLong as ReceiptIcon,
  Email as EmailIcon,
  CreditCard as CreditCardIcon,
  ArrowForward as ArrowForwardIcon,
  ViewList as ViewListIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AutorenewOutlined as AutorenewIcon
} from '@mui/icons-material';

// Format date to readable format
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

// Format currency with dollar sign
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const SuperAdminSubscriptions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    pendingSubscriptions: 0,
    canceledSubscriptions: 0,
    monthlyRevenue: 0
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Load data based on the selected tab
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch subscription plans to have plan details available
        const plansResponse = await stripeService.getPlatformPlans();
        if (plansResponse.success && Array.isArray(plansResponse.data)) {
          setPlans(plansResponse.data);
        }
        
        // Fetch all subscriptions
        const subscriptionsResponse = await stripeService.getAllSubscriptions();
        if (subscriptionsResponse?.data) {
          const subscriptionData = Array.isArray(subscriptionsResponse.data) 
            ? subscriptionsResponse.data 
            : [];
          
          setSubscriptions(subscriptionData);
          
          // Calculate basic stats from the subscription data
          const activeCount = subscriptionData.filter(sub => sub.status === 'active').length;
          const pendingCount = subscriptionData.filter(sub => ['past_due', 'incomplete', 'incomplete_expired'].includes(sub.status)).length;
          const canceledCount = subscriptionData.filter(sub => sub.status === 'canceled').length;
          
          // Calculate monthly revenue from active subscriptions
          const monthlyRevenue = subscriptionData
            .filter(sub => sub.status === 'active')
            .reduce((sum, sub) => sum + (parseFloat(sub.amount) || 0), 0);
          
          setStats({
            totalSubscriptions: subscriptionData.length,
            activeSubscriptions: activeCount,
            pendingSubscriptions: pendingCount,
            canceledSubscriptions: canceledCount,
            monthlyRevenue: monthlyRevenue
          });
        }
      } catch (err) {
        console.error("Error loading subscription data:", err);
        setError({
          message: `Failed to load subscription information: ${err.message || 'Unknown error'}`,
          details: err.response?.data?.message
        });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };
    
    loadData();
  }, [tabValue]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0); // Reset pagination when changing tabs
  };
  
  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Force reload by triggering the useEffect
      const subscriptionsResponse = await stripeService.getAllSubscriptions();
      if (subscriptionsResponse?.data) {
        setSubscriptions(Array.isArray(subscriptionsResponse.data) ? subscriptionsResponse.data : []);
      }
      setSuccessMsg('Data refreshed successfully');
    } catch (err) {
      setError({
        message: `Failed to refresh data: ${err.message || 'Unknown error'}`
      });
    } finally {
      setRefreshing(false);
    }
  };
  
  // Menu handlers
  const handleMenuOpen = (event, subscriptionId, tenantId) => {
    setAnchorEl(event.currentTarget);
    setSelectedSubscriptionId(subscriptionId);
    setSelectedTenantId(tenantId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSubscriptionId(null);
    setSelectedTenantId(null);
  };
  
  const handleViewDetails = () => {
    if (selectedSubscriptionId) {
      navigate(`/superadmin/subscriptions/${selectedSubscriptionId}`);
    }
    handleMenuClose();
  };
  
  const handleViewTenant = () => {
    if (selectedTenantId) {
      navigate(`/superadmin/tenants/${selectedTenantId}`);
    }
    handleMenuClose();
  };
  
  const handleViewPlans = () => {
    if (selectedTenantId) {
      navigate(`/superadmin/tenants/${selectedTenantId}/subscription`);
    }
    handleMenuClose();
  };
  
  const handleChangeTenantPlan = async () => {
    if (selectedTenantId) {
      try {
        // Get available plans for this tenant
        const availablePlansResponse = await stripeService.getAvailablePlansForTenant(selectedTenantId);
        
        // Navigate to plan selection page with tenant ID
        navigate(`/superadmin/tenants/${selectedTenantId}/change-plan`, { 
          state: { 
            availablePlans: availablePlansResponse.data,
            tenantId: selectedTenantId
          } 
        });
      } catch (err) {
        setError({
          message: `Failed to get available plans: ${err.message}`
        });
      }
    }
    handleMenuClose();
  };
  
  const handleCancelSubscription = async () => {
    if (!selectedSubscriptionId) {
      handleMenuClose();
      return;
    }
    
    try {
      setLoading(true);
      await stripeService.cancelSubscription(selectedSubscriptionId);
      
      // Reload subscriptions data
      const subscriptionsResponse = await stripeService.getAllSubscriptions();
      if (subscriptionsResponse?.data) {
        setSubscriptions(Array.isArray(subscriptionsResponse.data) ? subscriptionsResponse.data : []);
      }
      
      setSuccessMsg('Subscription canceled successfully');
    } catch (err) {
      setError({
        message: `Failed to cancel subscription: ${err.message}`
      });
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };
  
  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Filter subscriptions based on search term and current tab
  const filteredSubscriptions = useMemo(() => {
    let filtered = subscriptions;
    
    // Filter by tab
    if (tabValue === 1) { // Active
      filtered = filtered.filter(sub => sub.status === 'active');
    } else if (tabValue === 2) { // Past Due
      filtered = filtered.filter(sub => ['past_due', 'incomplete', 'incomplete_expired'].includes(sub.status));
    } else if (tabValue === 3) { // Canceled
      filtered = filtered.filter(sub => sub.status === 'canceled');
    }
    
    // Then filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(sub => 
        (sub.tenantName && sub.tenantName.toLowerCase().includes(searchLower)) ||
        (sub.planName && sub.planName.toLowerCase().includes(searchLower)) ||
        (sub.id && sub.id.toLowerCase().includes(searchLower)) ||
        (sub.customerEmail && sub.customerEmail.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [subscriptions, searchTerm, tabValue]);
  
  // Apply pagination
  const paginatedSubscriptions = useMemo(() => {
    return filteredSubscriptions.slice(
      page * rowsPerPage, 
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredSubscriptions, page, rowsPerPage]);
  
  // Get chip color and icon based on status
  const getStatusChipProps = (status) => {
    switch(status) {
      case 'active':
        return { color: 'success', icon: <CheckCircleIcon fontSize="small" /> };
      case 'past_due':
      case 'incomplete':
      case 'incomplete_expired':
        return { color: 'warning', icon: <WarningIcon fontSize="small" /> };
      case 'canceled':
      case 'unpaid':
        return { color: 'error', icon: <ErrorIcon fontSize="small" /> };
      default:
        return { color: 'default', icon: null };
    }
  };
  
  // Find plan details by plan ID
  const getPlanDetails = (planId) => {
    return plans.find(plan => plan.id === planId || plan.planId === planId) || {};
  };
  
  if (loading && !refreshing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 0 }}> {/* Removed padding here */}
      <Container maxWidth={false} sx={{ px: 0, ml: 0 }}> {/* Added ml: 0 to remove any margin */}
        {/* Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 4, 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ mr: 1 }} /> Platform Subscriptions
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8 }}>
                Manage and monitor all tenant subscriptions across the platform
              </Typography>
            </Box>
            <Box>
              <Button 
                variant="contained" 
                color="secondary" 
                startIcon={<AutorenewIcon />} 
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Display any errors */}
        {error && (
          <Alert 
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error.message}
            {error.details && <Typography variant="body2">{error.details}</Typography>}
          </Alert>
        )}

        {/* Success message */}
        <Snackbar
          open={!!successMsg}
          autoHideDuration={5000}
          onClose={() => setSuccessMsg('')}
          message={successMsg}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />

        {/* Subscription Stats */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Subscription Summary
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Total</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats.totalSubscriptions}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Active</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats.activeSubscriptions}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Past Due</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats.pendingSubscriptions}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'error.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Canceled</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats.canceledSubscriptions}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body1" color="text.secondary">Monthly Revenue</Typography>
                  <Typography variant="h5" color="primary" fontWeight="medium">
                    {formatCurrency(stats.monthlyRevenue)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Quick Actions
                </Typography>
                
                <List>
                  <ListItem button onClick={() => navigate('/superadmin/plans')} sx={{ borderRadius: 1, mb: 1 }}>
                    <ListItemIcon><ViewListIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Manage Subscription Plans" />
                  </ListItem>
                  
                  <ListItem button onClick={() => navigate('/superadmin/tenants')} sx={{ borderRadius: 1, mb: 1 }}>
                    <ListItemIcon><PeopleIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="View All Tenants" />
                  </ListItem>
                  
                  <ListItem button onClick={() => navigate('/superadmin/dashboard')} sx={{ borderRadius: 1 }}>
                    <ListItemIcon><DashboardIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Back to Dashboard" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Subscriptions Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="subscription tabs"
              sx={{ px: 2 }}
            >
              <Tab label="All Subscriptions" id="tab-0" />
              <Tab label="Active" id="tab-1" />
              <Tab label="Past Due" id="tab-2" />
              <Tab label="Canceled" id="tab-3" />
            </Tabs>
          </Box>
          
          <Box sx={{ 
            p: 2, 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {tabValue === 0 && 'All Subscriptions'}
              {tabValue === 1 && 'Active Subscriptions'}
              {tabValue === 2 && 'Past Due Subscriptions'}
              {tabValue === 3 && 'Canceled Subscriptions'}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                placeholder="Search subscriptions..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: '100%', sm: 220 } }}
              />
              
              <Tooltip title="Filter options">
                <IconButton>
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Divider />
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Renewal Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refreshing ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                        Refreshing data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedSubscriptions.length > 0 ? (
                  paginatedSubscriptions.map((subscription) => {
                    const statusProps = getStatusChipProps(subscription.status);
                    const planDetails = getPlanDetails(subscription.planId);
                    
                    return (
                      <TableRow key={subscription.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{subscription.tenantName || 'Unknown Tenant'}</Typography>
                          {subscription.customerEmail && (
                            <Typography variant="caption" color="text.secondary">
                              {subscription.customerEmail}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {planDetails.name || subscription.planName || 'Unknown Plan'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatCurrency(subscription.amount)}/{subscription.interval || 'month'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={statusProps.icon}
                            label={subscription.status?.replace('_', ' ') || 'Unknown'}
                            size="small" 
                            color={statusProps.color}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(subscription.currentPeriodEnd)}
                          {subscription.cancelAtPeriodEnd && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                              Will cancel
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            size="small" 
                            onClick={(event) => handleMenuOpen(event, subscription.id, subscription.tenantId)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      {searchTerm ? 'No matching subscriptions found.' : 'No subscriptions found.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredSubscriptions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        
        {/* Context menu for row actions */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewDetails}>View Subscription Details</MenuItem>
          <MenuItem onClick={handleViewTenant}>View Tenant Details</MenuItem>
          <MenuItem onClick={handleViewPlans}>View Tenant Subscription</MenuItem>
          <MenuItem onClick={handleChangeTenantPlan}>Change Plan</MenuItem>
          <Divider />
          <MenuItem onClick={handleCancelSubscription} sx={{ color: 'error.main' }}>
            Cancel Subscription
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
};

export default SuperAdminSubscriptions;
